import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../styles/test-system.css';
import './TestEnrolments.css';
import Navigation from '../components/Navigation';
import TestNav from '../components/tests/TestNav';
import ConfirmationDeadline from '../components/tests/ConfirmationDeadline';
import CycleStatusChip from '../components/tests/CycleStatusChip';
import EnrolmentStatusChip from '../components/tests/EnrolmentStatusChip';
import { confirmEnrolment, getMyCertificates, getMyEnrolments, readApiError } from '../api/testSystem';
import { LIVE_ENROLMENT_STATUSES, canOpenCycle, canOpenTrial, isPastCycle, trialHref } from '../lib/trialDoor';
import { isAuthenticated } from '../utils/auth';
import { formatInTimeZone, getTimeZoneLabel } from '../utils/dateUtils';
import { certificatesByCycle, getWindowOpensAt } from '../utils/testCycle';

const AlertIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
);

const InfoIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="16" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
);

const CheckIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

/* A cycle's scheduled moments, each on the cycle's own clock. */
const CycleMoments = ({ moments, timeZone }) => (
    <dl className="ts-facts ts-enrolment-facts">
        {moments.map(({ key, label, at }) => (
            <div className="ts-fact" key={key}>
                <dt>{label}</dt>
                <dd>
                    {formatInTimeZone(at, timeZone)}{' '}
                    <span className="ts-enrolment-zone">
                        {getTimeZoneLabel(at, timeZone)}
                    </span>
                </dd>
            </div>
        ))}
    </dl>
);

/* Why an entry went back to the balance, said the same way on a live card and a past one. */
const ReturnedEntryNote = ({ cycle, enrolment }) => {
    if (enrolment?.status === 'no_show') {
        return (
            <p className="ts-note ts-note--info">
                <InfoIcon />
                <span>
                    You did not confirm in time, so your spot was released and your entry
                    was returned to your balance.
                </span>
            </p>
        );
    }

    if (enrolment?.status === 'cancelled_credited') {
        /* Never `cancelled_reason`: it is an operator's note to the team, written for
           the team, and it has been read by candidates as if it were addressed to them. */
        return (
            <p className="ts-note ts-note--info">
                <InfoIcon />
                <span>
                    This cycle was cancelled. Your entry was returned to your balance.
                </span>
            </p>
        );
    }

    return null;
};

const EnrolmentRow = ({ cycle, enrolment, onConfirm, pending, error, confirmed }) => {
    const timeZone = cycle?.display_timezone;
    const windowOpensAt = getWindowOpensAt(cycle);

    const canConfirm = cycle?.status === 'confirmation' && enrolment?.status === 'enrolled';
    // A spot that was released or returned no longer opens the cycle's page — the server
    // answers 404 there — so the row says what happened and is not a link.
    const linked = canOpenCycle(enrolment, cycle);

    // A manual-start cohort has no `start_at` at all; once the team starts it, the
    // moment it actually began is `started_at`, and the tense follows.
    const started = Boolean(cycle.started_at);
    const moments = [
        {
            key: 'start',
            label: started ? 'Cycle started' : 'Cycle starts',
            at: cycle.start_at || cycle.started_at,
        },
        { key: 'ends', label: 'Cycle ends', at: cycle.ends_at },
        cycle.status === 'open' && { key: 'window', label: 'Confirmation opens', at: windowOpensAt },
    ].filter(entry => entry && entry.at);

    return (
        <article className="ts-panel ts-enrolment" data-testid="enrolment-row">
            <div className="ts-enrolment-head">
                <div className="ts-enrolment-heading">
                    <h2 className="ts-enrolment-title">
                        {linked ? (
                            <Link className="ts-enrolment-link" to={`/tests/cycles/${cycle.cycle_id}`}>
                                {cycle.structure_name || 'Test'}
                            </Link>
                        ) : (cycle.structure_name || 'Test')}
                    </h2>
                </div>
                <div className="ts-enrolment-chips">
                    <EnrolmentStatusChip status={enrolment?.status} />
                    {/* "Running" beside "Not confirmed in time" reads as if the student were on it. */}
                    {linked && <CycleStatusChip status={cycle.status} />}
                </div>
            </div>

            {moments.length > 0 ? (
                <CycleMoments moments={moments} timeZone={timeZone} />
            ) : canConfirm ? null : (
                /* When the confirm CTA is up, its own note already says the team starts
                   this one — saying it twice on one card is noise. */
                <p className="ts-enrolment-manual">
                    This cycle has no scheduled start yet — everyone enrolled is emailed when
                    the team starts it.
                </p>
            )}

            {confirmed && (
                <p className="ts-note ts-note--ok" role="status">
                    <CheckIcon />
                    <span>Your participation is confirmed. See you at the start.</span>
                </p>
            )}

            {error && (
                <p className="ts-note ts-note--danger" role="alert" data-testid="enrolment-error">
                    <AlertIcon />
                    <span>{error}</span>
                </p>
            )}

            {canConfirm && (
                <div className="ts-enrolment-confirm">
                    <ConfirmationDeadline cycle={cycle} />
                    <button
                        className="ts-btn ts-btn--primary"
                        onClick={() => onConfirm(cycle.cycle_id)}
                        disabled={pending}
                        data-testid={`confirm-${cycle.cycle_id}`}
                    >
                        {pending && <span className="ts-spinner-sm" />}
                        Confirm participation
                    </button>
                </div>
            )}

            <ReturnedEntryNote cycle={cycle} enrolment={enrolment} />

            {cycle.status === 'open' && enrolment?.status === 'enrolled' && (
                <p className="ts-note ts-note--info">
                    <InfoIcon />
                    <span>
                        You're enrolled. We'll email you when the confirmation window opens —
                        you have to confirm then to keep your spot.
                    </span>
                </p>
            )}

            {canOpenTrial(enrolment, cycle) && (
                <Link className="ts-btn ts-btn--primary" to={trialHref({ work_trial_id: enrolment.work_trial_id })}>
                    Open your work trial
                </Link>
            )}
        </article>
    );
};

/* A participation that is over: what it was, when it ran and how it ended, with no way back in. */
const PastTrialCard = ({ cycle, enrolment, certificate }) => {
    const moments = [
        { key: 'start', label: 'Cycle start', at: cycle.start_at || cycle.started_at },
        { key: 'end', label: 'Cycle end', at: cycle.ends_at || cycle.ended_at },
    ].filter(entry => entry.at);

    // Only a participation that was actually taken can be waiting on a result: a
    // no-show or a cancelled cohort never produced work to review.
    const awaitingCertificate = !certificate
        && cycle.status === 'ended'
        && LIVE_ENROLMENT_STATUSES.includes(enrolment?.status);

    return (
        <article className="ts-panel ts-enrolment ts-past-trial" data-testid="past-trial">
            <div className="ts-enrolment-head">
                <div className="ts-enrolment-heading">
                    <h3 className="ts-enrolment-title">{cycle.structure_name || 'Test'}</h3>
                </div>
                <div className="ts-enrolment-chips">
                    <EnrolmentStatusChip status={enrolment?.status} />
                </div>
            </div>

            {moments.length > 0 && <CycleMoments moments={moments} timeZone={cycle.display_timezone} />}

            <ReturnedEntryNote cycle={cycle} enrolment={enrolment} />

            {/* A cohort that ended and has handed back no certificate yet used to say
                nothing at all here, which reads as the participation having vanished.
                Why it has not arrived — marking still running, a team hold on the
                release — is the team's business; what the candidate is owed is that it
                is coming and where it will land. */}
            {awaitingCertificate && (
                <p className="ts-note ts-note--info" data-testid="certificate-pending">
                    <InfoIcon />
                    <span>
                        This cycle has ended and your work is being reviewed. Your
                        certificate appears under Certificates once it is ready, and we
                        will email you then.
                    </span>
                </p>
            )}

            {certificate && (
                <Link className="ts-btn" to="/tests/certificates" data-testid="past-trial-certificate">
                    View your certificate
                </Link>
            )}
        </article>
    );
};

const TestEnrolments = () => {
    const navigate = useNavigate();

    const [rows, setRows] = useState([]);
    const [certificates, setCertificates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [pendingCycle, setPendingCycle] = useState('');
    const [rowErrors, setRowErrors] = useState({});
    const [justConfirmed, setJustConfirmed] = useState({});

    const loadEnrolments = useCallback(async () => {
        setLoading(true);
        setLoadError('');

        try {
            // A certificates read that fails costs the past cards their link, not the page.
            const [enrolments, issued] = await Promise.all([
                getMyEnrolments(),
                getMyCertificates().catch(() => []),
            ]);
            setRows(enrolments);
            setCertificates(issued);
        } catch (e) {
            setLoadError(readApiError(e, 'Your enrolments could not be loaded.').message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!isAuthenticated()) {
            navigate('/login');
            return;
        }

        loadEnrolments();
    }, [navigate, loadEnrolments]);

    const handleConfirm = async (cycleId) => {
        setPendingCycle(cycleId);
        setRowErrors(errors => ({ ...errors, [cycleId]: '' }));

        try {
            const data = await confirmEnrolment(cycleId);
            setRows(current => current.map(row => (
                row.cycle?.cycle_id === cycleId && data?.enrolment
                    ? { ...row, enrolment: data.enrolment }
                    : row
            )));
            setJustConfirmed(flags => ({ ...flags, [cycleId]: true }));
        } catch (e) {
            const { message } = readApiError(e, 'Your participation could not be confirmed.');
            setRowErrors(errors => ({ ...errors, [cycleId]: message }));
            // The window may have closed under the page; whatever the backend now
            // says about this enrolment is the truth.
            loadEnrolments();
        }

        setPendingCycle('');
    };

    const listed = rows.filter(row => row.cycle?.cycle_id);
    const liveRows = listed.filter(row => !isPastCycle(row.cycle));
    // A cohort that is over is kept as a record under Past trials, never as a door.
    const pastRows = listed.filter(row => isPastCycle(row.cycle));
    const pastCertificates = certificatesByCycle(pastRows, certificates);

    return (
        <>
            <Navigation />
            <div className="ts-page">
                <div className="ts-shell ts-shell--wide">
                    <TestNav />

                    <header className="ts-head">
                        <div className="ts-head-main">
                            <h1 className="ts-title">My enrolments</h1>
                            <p className="ts-lede">
                                Every cycle you have enrolled on. When a cycle's confirmation window
                                opens you have to confirm here to keep your spot.
                            </p>
                        </div>
                    </header>

                    {loading ? (
                        <div className="ts-page-state ts-page-state--loading" data-testid="enrolments-loading">
                            <div className="ts-spinner" />
                            <p>Loading your enrolments…</p>
                        </div>
                    ) : loadError ? (
                        <div className="ts-page-state" role="alert">
                            <h2>Your enrolments are unavailable</h2>
                            <p>{loadError}</p>
                            <button className="ts-btn" onClick={loadEnrolments}>Try again</button>
                        </div>
                    ) : rows.length === 0 ? (
                        <div className="ts-page-state" data-testid="enrolments-empty">
                            <h2>You are not enrolled on any cycle</h2>
                            <p>Anything you enrol on shows up here, with what you have to do next.</p>
                            <Link className="ts-btn" to="/tests">Browse the catalog</Link>
                        </div>
                    ) : (
                        <>
                            {liveRows.length > 0 && (
                                <div className="ts-enrolment-list" data-testid="enrolments-list">
                                    {liveRows.map(({ cycle, enrolment }) => (
                                        <EnrolmentRow
                                            key={cycle.cycle_id}
                                            cycle={cycle}
                                            enrolment={enrolment}
                                            onConfirm={handleConfirm}
                                            pending={pendingCycle === cycle.cycle_id}
                                            error={rowErrors[cycle.cycle_id]}
                                            confirmed={justConfirmed[cycle.cycle_id]}
                                        />
                                    ))}
                                </div>
                            )}

                            {pastRows.length > 0 && (
                                <section className="ts-past" aria-labelledby="past-trials-title" data-testid="past-trials">
                                    <div className="ts-group-head">
                                        <h2 className="ts-panel-title" id="past-trials-title">Past trials</h2>
                                        <span className="ts-count">{pastRows.length}</span>
                                    </div>
                                    <div className="ts-enrolment-list">
                                        {pastRows.map(({ cycle, enrolment }) => (
                                            <PastTrialCard
                                                key={cycle.cycle_id}
                                                cycle={cycle}
                                                enrolment={enrolment}
                                                certificate={pastCertificates[cycle.cycle_id]}
                                            />
                                        ))}
                                    </div>
                                </section>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default TestEnrolments;
